import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Modal, Table, Spin, Row, Col, Button, message } from 'antd'
import type { DataType } from '../interface'
import {
  VehicleModalActions,
  VehicleModalContainer,
  VehicleModalInfo,
  VehicleModalInfoItem,
} from '../styles'
import dayjs from 'dayjs'
import fetchAPI from '../../../../../lib/utils/fetch-api'
import { DownloadOutlined } from '@ant-design/icons'
import JSZip from 'jszip'
import moment from 'moment/moment'
import { TableContainer } from '../../../../components/data-table/styles'
import { Link } from 'react-router-dom'
import { formatNumber } from '../utils'

interface RouteSheetsCardModalProps {
  routeSheet: DataType | null
  onClose: () => void
}

export const RouteSheetsCardModal: React.FC<RouteSheetsCardModalProps> = ({
  routeSheet,
  onClose,
}) => {
  const [orders, setOrders] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const isModalVisible = !!routeSheet

  useEffect(() => {
    if (!routeSheet?.id) {
      setOrders([])
      return
    }

    setLoading(true)
    fetchAPI(`/api/logistics/route-sheets/${routeSheet.id}`)
      .then(response => {
        setOrders(response.data || [])
      })
      .catch(error => {
        console.error('Ошибка при загрузке заказов:', error)
        setOrders([])
      })
      .finally(() => setLoading(false))
  }, [routeSheet?.id])

  const modalColumns = useMemo(
    () => [
      {
        title: '№',
        dataIndex: 'sequence_in_run',
        key: 'sequence_in_run',
      },
      {
        title: 'Номер ТЗ',
        dataIndex: 'order_reference',
        key: 'order_reference',
        width: 120,
        render: (text: string, record: any) => (
          <Link to={`/logistics/deliveries/${record.id}`}>{text}</Link>
        ),
      },
      {
        title: 'Дилер',
        dataIndex: 'dealer',
        key: 'dealer',
        width: 150,
      },
      {
        title: 'Адрес',
        dataIndex: 'customer_location_address',
        key: 'customer_location_address',
        render: (text, record) => (
          <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>
        ),
        width: 180,
      },
      {
        title: 'Телефон клиента',
        dataIndex: 'client_phone',
        key: 'client_phone',
        width: 120,
      },
      {
        title: 'Вес',
        dataIndex: 'capacity',
        key: 'capacity',
        width: 100,
        render: value => (value ? value + 'кг' : ''),
      },
      {
        title: 'Объем',
        dataIndex: 'volume',
        key: 'volume',
        width: 100,
        render: value => (value ? value + 'м²' : ''),
      },
      {
        title: 'Кол-во',
        dataIndex: 'cost',
        key: 'cost',
        width: 80,
      },
      {
        title: 'Дополнительные инструкции',
        dataIndex: 'additional_instructions',
        key: 'additional_instructions',
        render: (text: string, record: any) => {
          const style: React.CSSProperties = record.highlight_comment
            ? { backgroundColor: '#fff', color: '#f00', fontWeight: 700, padding: '4px 8px' }
            : {}
          return <span style={style}>{text || ''}</span>
        },
        width: 170,
      },
      {
        title: 'Окно заказа',
        dataIndex: 'drop_window_start',
        key: 'drop_window_start',
        render: (value: string, record: any) =>
          value && record.drop_window_end
            ? dayjs(value).format('HH:mm') + ' - ' + dayjs(record.drop_window_end).format('HH:mm')
            : '-',
        width: 120,
      },
      {
        title: 'Подъем',
        dataIndex: 'lifting',
        key: 'lifting',
        width: 120,
      },
      {
        title: 'Сумма доставки',
        dataIndex: 'shipment_amount',
        key: 'shipment_amount',
        width: 100,
      },
      {
        title: 'Доплата за спуск/подъем',
        dataIndex: 'lift_amount',
        key: 'lift_amount',
        width: 100,
      },
      {
        title: 'Сумма за доставку за пред. тариф.зоны',
        dataIndex: 'delivery_outside_amount',
        key: 'delivery_outside_amount',
        width: 100,
      },
      {
        title: 'Доплата за габариты (машины)',
        dataIndex: 'size_amount',
        key: 'size_amount',
        width: 100,
      },
      {
        title: 'Доплата грузчикам',
        dataIndex: 'loader_amount',
        key: 'loader_amount',
        width: 100,
      },
      {
        title: 'Прочие расходы',
        dataIndex: 'other_amount',
        key: 'other_amount',
        width: 100,
      },
      {
        title: 'Затраты логистики',
        dataIndex: 'logistics_amount',
        key: 'logistics_amount',
        width: 100,
      },
      {
        title: 'Сумма Итого',
        dataIndex: 'delivery_amount',
        key: 'delivery_amount',
        width: 100,
        render: val => (val ? <b>{val}</b> : ''),
      },
    ],
    [],
  )

  const totals = useMemo(
    () => ({
      weight: orders.reduce((acc, order) => acc + (Number(order.capacity) || 0), 0),
      volume: orders.reduce((acc, order) => acc + (Number(order.volume) || 0), 0),
      deliveryAmount: orders.reduce((acc, order) => acc + (Number(order.delivery_amount) || 0), 0),
    }),
    [orders],
  )

  const handleDownload = useCallback(async () => {
    if (!routeSheet?.id) {
      return
    }

    setIsDownloading(true)
    const zip = new JSZip()

    try {
      const { data } = await fetchAPI(`/api/logistics/route-sheets/${routeSheet.id}/files`)

      if (!data || !data.length) {
        message.info('Нет файлов для загрузки')
        setIsDownloading(false)
        return
      }

      const filePromises = data.map(async file => {
        try {
          const fileResponse = await fetch(file.url)
          if (!fileResponse.ok) {
            console.warn(`Не удалось загрузить файл: ${file.name}`)
            return null
          }
          const blob = await fileResponse.blob()
          return { blob, name: file.name || `file_${Date.now()}` }
        } catch (err) {
          console.warn(`Ошибка загрузки файла ${file.name}:`, err)
          return null
        }
      })

      const files = (await Promise.all(filePromises)).filter(file => file !== null)

      if (!files.length) {
        message.warning('Не удалось загрузить ни один файл')
        setIsDownloading(false)
        return
      }

      files.forEach(({ blob, name }) => {
        zip.file(name, blob)
      })

      const content = await zip.generateAsync({ type: 'blob' })
      const { saveAs } = await import('file-saver')
      saveAs(content, `маршрут_${routeSheet.id}_${moment().format('DD-MM-YYYY_HH-mm-ss')}.zip`)
      message.success('Архив успешно скачан')
    } catch (error) {
      console.error('Ошибка при создании архива:', error)
      message.error('Ошибка при создании архива')
    } finally {
      setIsDownloading(false)
    }
  }, [routeSheet?.id])

  if (!routeSheet) return null
  console.log('routeSheet', routeSheet)
  return (
    <Modal
      title={`Маршрутный лист #${routeSheet.id} - ${routeSheet.vehicle}`}
      open={isModalVisible}
      onCancel={onClose}
      width={1900}
      footer={null}
    >
      <VehicleModalContainer>
        <Spin spinning={loading}>
          <VehicleModalInfo>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <VehicleModalInfoItem>
                  <b>Дата:</b> {routeSheet.date ? dayjs(routeSheet.date).format('DD.MM.YYYY') : ''}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Загрузка:</b> {routeSheet.loading_time}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Возвращение:</b> {routeSheet.return_time}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Группы:</b> {routeSheet.zone_groups}
                </VehicleModalInfoItem>
              </Col>
              <Col span={6}>
                <VehicleModalInfoItem>
                  <b>Склад:</b> {routeSheet.center_name}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Машина:</b> {routeSheet.vehicle}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Водитель:</b> {routeSheet.driver_name}
                </VehicleModalInfoItem>
              </Col>
              <Col span={6}>
                <VehicleModalInfoItem>
                  <b>Суммарное время:</b> {routeSheet.total_travel_time}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Расстояние:</b> {formatNumber(routeSheet.distance, 'км')}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Кол-во заказов:</b> {routeSheet.orders_count}
                </VehicleModalInfoItem>
              </Col>
              <Col span={6}>
                <VehicleModalInfoItem>
                  <b>Вес фактический:</b> {formatNumber(routeSheet.total_weight_fact, 'кг')}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Вес рассчитанный:</b> {formatNumber(routeSheet.total_weight, 'кг')}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Итоговый объем:</b> {formatNumber(routeSheet.total_volume, 'м³')}
                </VehicleModalInfoItem>
                <VehicleModalInfoItem>
                  <b>Сумма доставки:</b> {formatNumber(routeSheet.total_shipment_amount, 'руб.')}
                </VehicleModalInfoItem>
              </Col>
            </Row>
          </VehicleModalInfo>

          {routeSheet.has_files && (
            <VehicleModalActions>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={isDownloading}
                disabled={isDownloading || !routeSheet.id}
              >
                Скачать фото
              </Button>
            </VehicleModalActions>
          )}
          <TableContainer className={'route-sheets-table'}>
            <Table
              columns={modalColumns}
              dataSource={orders.sort(
                (a, b) => Number(a.sequence_in_run) - Number(b.sequence_in_run),
              )}
              rowKey="id"
              size="small"
              bordered
              pagination={false}
            />
          </TableContainer>
        </Spin>
      </VehicleModalContainer>
    </Modal>
  )
}
